import { merge } from "lodash-es";
import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import {
  DeleteEventBlockModal,
  DeleteEventModal,
  DownloadGuestlistBlockModal,
} from "Bands/containers";
import { DownloadScheduleBlockModal } from "Bands/containers/DownloadScheduleBlockModal/DownloadScheduleBlockModal";
import { EmailGuestlistBlockModal } from "Bands/containers/EmailGuestlistBlockModal/EmailGuestlistBlockModal";
import { EditEventBlockTitleModal } from "Bands/pages/event/containers/EditEventBlockTitleModal";
import { BAND_PATHS } from "Bands/router";
import {
  bandSettingsSelector,
  clearDataByKey,
  dataMetaSelector,
  deleteEventGuestlistBlock,
  deleteEventInfoBlock,
  deleteEventPeopleBlock,
  deleteEventScheduleBlock,
  deleteEventTravelBlock,
  deleteEventVenueBlock,
  dispatchGetEventAction,
  dispatchGetEventGuestlistBlocks,
  dispatchGetEventInfoBlocks,
  dispatchGetEventPeopleBlocks,
  dispatchGetEventScheduleBlocks,
  dispatchGetEventTravelBlocks,
  dispatchGetEventVenueBlocks,
  editEventGuestlistBlockTitle,
  editEventPeopleBlockTitle,
  editEventScheduleBlockTitle,
  editEventTravelBlockTitle,
  eventBlocksSelectorCreator,
  eventMetaSelector,
  eventSelectorCreator,
} from "Bands/state";
import {
  deleteEventAccommodationBlock,
  dispatchGetEventAccommodationBlocks,
  editAccommodationBlockTitle,
} from "Bands/state/data/actions/eventBlocks/eventAccommodationBlock";
import {
  BAND_ACTIONS,
  EventBlocks,
  EventUniqueBlocks,
  IAccommodationBlock,
  IEvent,
  IEventBlock,
  IEventInfoBlock,
  IEventPeopleBlock,
  IEventTravelBlock,
  IGuestlistBlock,
  IIconBandSetting,
  IScheduleBlock,
  IVenueBlock,
} from "Bands/types";
import { getEventBlockActions, useEventIdFromPath } from "Bands/utils";
import { Clock, Guests, Hotel, Plane, User, Venue } from "shared/assets/icons";
import { API_ENDPOINTS } from "shared/constants";
import { loggerService } from "shared/services";
import { IAPIState, IDataKeys } from "shared/types";
import {
  buildUrl,
  getIcon,
  getIdFromPath,
  IUserPermissions,
  mapUserPermissions,
} from "shared/utils";
import { useBandContext } from ".";

interface EventContextState {
  eventId: string | null;
  eventPath: string | null;
  event: IEvent | null;
  apiMeta?: {
    status: IAPIState;
    error?: any;
  } | null;
  openDeleteModal: () => void;
  userPermissions: IUserPermissions;
  eventBlocks: IEventBlock[] | null;
  hasBlocks: { [key: string]: boolean } | null;
  fetchingEventBlocks: boolean;
  showPushCopyModal: IEventBlock | null;
  setShowPushCopyModal: (showPushCopyModal: IEventBlock | null) => void;
  refreshAllEventBlocks: () => void;
}

const initialState: EventContextState = {
  eventId: null,
  eventPath: null,
  event: null,
  apiMeta: null,
  openDeleteModal: () => {},
  userPermissions: {},
  eventBlocks: null,
  hasBlocks: null,
  fetchingEventBlocks: false,
  showPushCopyModal: null,
  setShowPushCopyModal: () => {},
  refreshAllEventBlocks: () => {},
};

const EventContext = createContext<EventContextState>(initialState);

interface EventProviderProps {
  eventId?: string;
}

export const EventProvider: FC<EventProviderProps> = ({
  children,
  eventId: propsId,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { bandId } = useBandContext();
  const eventId = useEventIdFromPath() || propsId!;
  const eventPath = useMemo(
    () => (eventId ? API_ENDPOINTS.EVENT(eventId) : ""),
    [eventId]
  );

  const { api: apiMeta } = useSelector(eventMetaSelector(eventId as string));

  const eventIdSelector = useMemo(
    () => eventSelectorCreator(eventId || ""),
    [eventId]
  );
  const event = useSelector(eventIdSelector);

  const { api: peopleBlocksApi } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getEventPeopleBlocks)
  );

  const { api: scheduleBlocksApi } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getEventScheduleBlocks)
  );

  const { api: infoBlocksApi } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getEventInfoBlocks)
  );

  const { api: venueBlocksApi } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getEventVenueBlocks)
  );

  const { api: guestlistBlocksApi } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getEventGuestlistBlocks)
  );

  const { api: travelBlocksApi } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getEventTravelBlocks)
  );

  const { api: accommodationBlocksApi } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getEventAccommodationBlocks)
  );

  const fetchingEventBlocks = useMemo(() => {
    return (
      peopleBlocksApi?.status === IAPIState.pending ||
      scheduleBlocksApi?.status === IAPIState.pending ||
      infoBlocksApi?.status === IAPIState.pending ||
      venueBlocksApi?.status === IAPIState.pending ||
      guestlistBlocksApi?.status === IAPIState.pending ||
      travelBlocksApi?.status === IAPIState.pending ||
      accommodationBlocksApi?.status === IAPIState.pending
    );
  }, [
    peopleBlocksApi,
    scheduleBlocksApi,
    infoBlocksApi,
    venueBlocksApi,
    guestlistBlocksApi,
    travelBlocksApi,
    accommodationBlocksApi,
  ]);

  // event blocks
  const [showEditEventBlockTitleModal, setShowEditEventBlockTitleModal] =
    useState<IEventBlock | null>();
  const [showDeleteEventBlockModal, setShowDeleteEventBlockModal] =
    useState<IEventBlock | null>();
  const [showDownloadGuestlistModal, setShowDownloadGuestlistModal] =
    useState<IGuestlistBlock | null>();
  const [showEmailGuestlistModal, setShowEmailGuestlistModal] =
    useState<IGuestlistBlock | null>();
  const [showDownloadScheduleModal, setShowDownloadScheduleModal] =
    useState<IScheduleBlock | null>();
  const [showPushCopyModal, setShowPushCopyModal] =
    useState<IEventBlock | null>(null);

  // people block
  const peopleBlocksSelector = useMemo(
    () => eventBlocksSelectorCreator(EventUniqueBlocks.people),
    []
  );

  const eventPeopleBlocks = useSelector(
    peopleBlocksSelector
  ) as IEventPeopleBlock[];

  // schedule block
  const scheduleBlocksSelector = useMemo(
    () => eventBlocksSelectorCreator(EventBlocks.schedule),
    []
  );
  const eventScheduleBlocks = useSelector(
    scheduleBlocksSelector
  ) as IScheduleBlock[];

  // info blocks (custom)
  const infoBlocksSelector = useMemo(
    () => eventBlocksSelectorCreator(EventBlocks.infoBlock),
    []
  );
  const eventInfoBlocks = useSelector(infoBlocksSelector) as IEventInfoBlock[];

  // venue blocks
  const venueBlocksSelector = useMemo(
    () => eventBlocksSelectorCreator(EventUniqueBlocks.venue),
    []
  );
  const eventVenueBlocks = useSelector(venueBlocksSelector) as IVenueBlock[];

  // guestlist blocks
  const guestlistBlocksSelector = useMemo(
    () => eventBlocksSelectorCreator(EventBlocks.guestlist),
    []
  );
  const eventGuestlistBlocks = useSelector(
    guestlistBlocksSelector
  ) as IGuestlistBlock[];

  // travel blocks
  const travelBlocksSelector = useMemo(
    () => eventBlocksSelectorCreator(EventBlocks.travel),
    []
  );
  const eventTravelBlocks = useSelector(
    travelBlocksSelector
  ) as IEventTravelBlock[];

  // accommodation blocks
  const accommodationBlocksSelector = useMemo(
    () => eventBlocksSelectorCreator(EventBlocks.accommodation),
    []
  );
  const eventAccommodationBlocks = useSelector(
    accommodationBlocksSelector
  ) as IAccommodationBlock[];

  const eventInfoBlockTypes = useSelector(
    bandSettingsSelector<IIconBandSetting>(IDataKeys.eventInfoBlockTypes)
  ) as IIconBandSetting[];

  const eventBlocksWithoutActions = useMemo((): IEventBlock[] => {
    if (!bandId || !eventId) return [];

    interface GetPropsProps {
      id: string;
      type: string;
      editUrl: string;
    }

    const props = ({ id, editUrl }: GetPropsProps) => ({
      sidemenuId: `sidemenu_${id}`,
      path: buildUrl(
        BAND_PATHS.BAND_EVENT,
        {
          bandId,
          eventId,
        },
        id
      ),
      handleEdit: () =>
        history.push(
          buildUrl(editUrl, {
            bandId,
            eventId,
            blockId: id,
          })
        ),
    });

    const sortAlphabeticallyAndWithNumbers = (
      a: IEventBlock | IEventInfoBlock,
      b: IEventBlock | IEventInfoBlock
    ) => {
      const aTitle = a.title.toLowerCase();
      const bTitle = b.title.toLowerCase();
      const aNum = parseInt(aTitle.match(/\d+$/)?.[0] || "0");
      const bNum = parseInt(bTitle.match(/\d+$/)?.[0] || "0");
      if (aNum === bNum) {
        return aTitle.localeCompare(bTitle);
      }
      return aNum - bNum;
    };

    return [
      ...eventVenueBlocks
        ?.map(
          ({ id, title, permissions }) =>
            merge(
              {},
              props({
                id,
                editUrl: BAND_PATHS.EDIT_VENUE_BLOCK,
                type: EventUniqueBlocks.venue,
              }),
              {
                id,
                title,
                permissions,
                handleDelete: deleteEventVenueBlock,
                deleteAction: BAND_ACTIONS.deleteEventVenueBlock,
                type: EventUniqueBlocks.venue,
                icon: Venue,
              }
            ) as unknown as IEventBlock
        )
        .sort(sortAlphabeticallyAndWithNumbers),
      ...eventPeopleBlocks
        ?.map(
          ({ id, title, permissions }) =>
            merge(
              {},
              props({
                id,
                editUrl: BAND_PATHS.EDIT_PEOPLE_BLOCK,
                type: EventUniqueBlocks.people,
              }),
              {
                id,
                title,
                permissions,
                handleEditTitle: editEventPeopleBlockTitle,
                editTitleAction: BAND_ACTIONS.editEventPeopleBlockTitle,
                handleDelete: deleteEventPeopleBlock,
                deleteAction: BAND_ACTIONS.deleteEventPeopleBlock,
                type: EventUniqueBlocks.people,
                icon: User,
              }
            ) as unknown as IEventBlock
        )
        .sort(sortAlphabeticallyAndWithNumbers),
      ...eventScheduleBlocks
        ?.map(
          ({ id, title, permissions }) =>
            merge(
              {},
              props({
                id,
                editUrl: BAND_PATHS.EDIT_SCHEDULE_BLOCK,
                type: EventBlocks.schedule,
              }),
              {
                id,
                title,
                permissions,
                handleEditTitle: editEventScheduleBlockTitle,
                editTitleAction: BAND_ACTIONS.editEventScheduleBlockTitle,
                handleDelete: deleteEventScheduleBlock,
                deleteAction: BAND_ACTIONS.deleteEventScheduleBlock,
                type: EventBlocks.schedule,
                icon: Clock,
              }
            ) as unknown as IEventBlock
        )
        .sort(sortAlphabeticallyAndWithNumbers),
      ...eventGuestlistBlocks
        ?.map(
          ({ id, title, permissions, ...rest }) =>
            merge(
              {},
              props({
                id,
                editUrl: BAND_PATHS.EDIT_GUESTLIST_BLOCK,
                type: EventBlocks.guestlist,
              }),
              {
                id,
                title,
                permissions,
                ...rest,
                handleDelete: deleteEventGuestlistBlock,
                handleEditTitle: editEventGuestlistBlockTitle,
                editTitleAction: BAND_ACTIONS.editEventGuestlistBlockTitle,
                deleteAction: BAND_ACTIONS.deleteEventGuestlistBlock,
                type: EventBlocks.guestlist,
                icon: Guests,
              }
            ) as unknown as IEventBlock
        )
        .sort(sortAlphabeticallyAndWithNumbers),
      ...eventTravelBlocks
        ?.map(
          ({ id, title, permissions }) =>
            merge(
              {},
              props({
                id,
                editUrl: BAND_PATHS.EDIT_TRAVEL_BLOCK,
                type: EventBlocks.travel,
              }),
              {
                id,
                title,
                permissions,
                handleEditTitle: editEventTravelBlockTitle,
                editTitleAction: BAND_ACTIONS.editEventTravelBlockTitle,
                handleDelete: deleteEventTravelBlock,
                deleteAction: BAND_ACTIONS.deleteEventTravelBlock,
                type: EventBlocks.travel,
                icon: Plane,
              }
            ) as unknown as IEventBlock
        )
        .sort(sortAlphabeticallyAndWithNumbers),
      ...eventAccommodationBlocks
        ?.map(
          ({ id, title, permissions }) =>
            merge(
              {},
              props({
                id,
                editUrl: BAND_PATHS.EDIT_ACCOMMODATION_BLOCK,
                type: EventBlocks.accommodation,
              }),
              {
                id,
                title,
                permissions,
                handleEditTitle: editAccommodationBlockTitle,
                editTitleAction: BAND_ACTIONS.editAccommodationBlockTitle,
                handleDelete: deleteEventAccommodationBlock,
                deleteAction: BAND_ACTIONS.deleteAccommodationBlock,
                type: EventBlocks.accommodation,
                icon: Hotel,
              }
            ) as unknown as IEventBlock
        )
        .sort(sortAlphabeticallyAndWithNumbers),
      ...eventInfoBlocks
        ?.sort(sortAlphabeticallyAndWithNumbers)
        ?.sort((a, b) => {
          const aIndex = eventInfoBlockTypes.findIndex(
            (type) =>
              getIdFromPath(type.id, API_ENDPOINTS.EVENT_INFO_BLOCK_TYPES) ===
              a.type.id
          );
          const bIndex = eventInfoBlockTypes.findIndex(
            (type) =>
              getIdFromPath(type.id, API_ENDPOINTS.EVENT_INFO_BLOCK_TYPES) ===
              b.type.id
          );

          return aIndex - bIndex;
        })
        ?.map(
          ({ id, title, permissions, type }) =>
            merge(
              {},
              props({
                id,
                editUrl: buildUrl(BAND_PATHS.EDIT_INFO_BLOCK, {
                  blockTypeId: type.id,
                }),
                type: EventBlocks.infoBlock,
              }),
              {
                id,
                title,
                permissions,
                handleDelete: deleteEventInfoBlock,
                deleteAction: BAND_ACTIONS.deleteEventInfoBlock,
                type: EventBlocks.infoBlock,
                icon: getIcon(type.icon),
              }
            ) as unknown as IEventBlock
        ),
    ];
  }, [
    bandId,
    eventId,
    eventVenueBlocks,
    eventPeopleBlocks,
    eventScheduleBlocks,
    eventGuestlistBlocks,
    eventTravelBlocks,
    eventAccommodationBlocks,
    eventInfoBlocks,
    history,
    eventInfoBlockTypes,
  ]);

  const eventBlocks = useMemo(
    () =>
      eventBlocksWithoutActions.map((block) => ({
        ...block,
        actions: getEventBlockActions({
          block,
          setShowEditEventBlockTitleModal,
          setShowDeleteEventBlockModal,
          setShowDownloadGuestlistModal,
          setShowEmailGuestlistModal,
          setShowDownloadScheduleModal,
          setShowPushCopyModal,
        }),
      })),
    [eventBlocksWithoutActions]
  );

  // check if 1 block exists for unique blocks
  const hasBlocks = useMemo(
    () => ({
      [EventUniqueBlocks.people]: !!eventPeopleBlocks?.length,
      [EventUniqueBlocks.venue]: !!eventVenueBlocks?.length,
    }),
    [eventPeopleBlocks, eventVenueBlocks]
  );

  const refreshAllEventBlocks = useCallback(() => {
    if (eventId) {
      dispatch(clearDataByKey(IDataKeys.eventPeopleBlock));
      dispatch(clearDataByKey(IDataKeys.eventScheduleBlock));
      dispatch(clearDataByKey(IDataKeys.eventInfoBlock));
      dispatch(clearDataByKey(IDataKeys.eventVenueBlock));
      dispatch(clearDataByKey(IDataKeys.eventGuestListBlock));
      dispatch(clearDataByKey(IDataKeys.eventTravelBlock));
      dispatch(clearDataByKey(IDataKeys.eventAccommodationBlock));

      dispatchGetEventAction(dispatch, eventId);
      dispatchGetEventPeopleBlocks(dispatch, eventId);
      dispatchGetEventScheduleBlocks(dispatch, eventId);
      dispatchGetEventInfoBlocks(dispatch, eventId);
      dispatchGetEventVenueBlocks(dispatch, eventId);
      dispatchGetEventGuestlistBlocks(dispatch, eventId);
      dispatchGetEventTravelBlocks(dispatch, eventId);
      dispatchGetEventAccommodationBlocks(dispatch, eventId);
    }
  }, [dispatch, eventId]);

  useEffect(() => {
    refreshAllEventBlocks();

    // clear blocks on unmount
    return () => {
      dispatch(clearDataByKey(IDataKeys.eventPeopleBlock));
      dispatch(clearDataByKey(IDataKeys.eventScheduleBlock));
      dispatch(clearDataByKey(IDataKeys.eventInfoBlock));
      dispatch(clearDataByKey(IDataKeys.eventVenueBlock));
      dispatch(clearDataByKey(IDataKeys.eventGuestListBlock));
      dispatch(clearDataByKey(IDataKeys.eventTravelBlock));
      dispatch(clearDataByKey(IDataKeys.eventAccommodationBlock));
    };
  }, [refreshAllEventBlocks, dispatch]);

  const openDeleteModal = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const userPermissions = useMemo(
    () => (event?.permissions ? mapUserPermissions(event.permissions) : {}),
    [event?.permissions]
  );

  // debug
  const renderCount = useRef<number>(0);
  useEffect(() => {
    renderCount.current = renderCount.current + 1;

    loggerService.log({
      icon: "🔄",
      title: "Renders",
      body: {
        renderCount: renderCount.current,
        event,
      },
    });
  }, [event]);

  return (
    <EventContext.Provider
      value={{
        eventId,
        eventPath,
        event,
        apiMeta,
        openDeleteModal,
        userPermissions,
        eventBlocks,
        hasBlocks,
        fetchingEventBlocks,
        refreshAllEventBlocks,
        showPushCopyModal,
        setShowPushCopyModal,
      }}
    >
      {showDeleteModal && (
        <DeleteEventModal handleClose={() => setShowDeleteModal(false)} />
      )}
      {showEditEventBlockTitleModal && (
        <EditEventBlockTitleModal
          block={showEditEventBlockTitleModal}
          handleClose={() => setShowEditEventBlockTitleModal(null)}
        />
      )}
      {showDeleteEventBlockModal && (
        <DeleteEventBlockModal
          block={showDeleteEventBlockModal}
          handleClose={() => setShowDeleteEventBlockModal(null)}
        />
      )}
      {showDownloadGuestlistModal && (
        <DownloadGuestlistBlockModal
          block={showDownloadGuestlistModal}
          handleClose={() => setShowDownloadGuestlistModal(null)}
        />
      )}
      {showEmailGuestlistModal && (
        <EmailGuestlistBlockModal
          block={showEmailGuestlistModal}
          handleClose={() => setShowEmailGuestlistModal(null)}
        />
      )}
      {showDownloadScheduleModal && (
        <DownloadScheduleBlockModal
          block={showDownloadScheduleModal}
          handleClose={() => setShowDownloadScheduleModal(null)}
        />
      )}
      {children}
    </EventContext.Provider>
  );
};

export const useEventContext = () => useContext(EventContext);
